import type { EntityFormErrorMap } from '@klear/ui/entity-form-state'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import isError from 'lodash/isError'
import noop from 'lodash/noop'
import values from 'lodash/values'
import { Check, Plus } from 'lucide-react'
import { CREDIT_CLASSIFICATION_IG, CREDIT_CLASSIFICATION_INSURED } from '@klear/constants'
import { creditReadToForm } from '@klear/credit/wire.client'
import { Card, CardContent, CardHeader, CardTitle } from '@klear/ui/components/card'
import { Combobox } from '@klear/ui/components/combobox'
import { Label } from '@klear/ui/components/label'
import { useTraders } from '@klear/ui/hooks/use-traders'
import { useEntityWizardForm } from '@klear/ui/hooks/use-entity-wizard-form'
import { hasEntityFormErrors } from '@klear/ui/entity-form-state'
import type { WizardStepGroupNavState } from '@klear/ui/wizard/nav-state'
import { bindWizardSteps, useWizardFlow, type WizardStepModule } from '@klear/ui/wizard/flow'
import { WizardFlowContent } from '@klear/ui/wizard/flow-content'
import { useFlowWizardNav } from '@klear/ui/wizard/use-step-group-nav'
import { useCustomerMutations } from '@klear/customer-ui/use-customers'
import { useCreditMutations } from '@klear/customer-ui/use-credit'
import {
  EMPTY_CUSTOMER_FORM,
  creditSubmitNeeded,
  formValuesToCreateData,
  formValuesToCreditCreateData,
  formValuesToCreditUpdateData,
  formValuesToUpdateData,
  customerToFormValues,
  type CreditRecord,
  type CustomerRecord,
  type CustomerWizardFormDirty,
  type CustomerWizardFormValues
} from '@klear/customer-ui/customer-commit-form'
import {
  clearCustomerFormErrors,
  markCustomerFormDirtyPaths,
  setCustomerFormField,
  type CustomerFormFieldPath,
  type CustomerFormFieldValue
} from '@klear/customer-ui/customer-form-state'
import {
  CustomerCreditFields,
  CustomerIdentifierFields,
  CustomerLocationFields,
  CustomerOrganizationFields,
  type CustomerFormUpdate
} from '@klear/customer-ui/customer-field-groups'
import { CompanyLookup } from '@klear/organization-ui/company-lookup'
import { AllianzCoverRequestsCard } from '@klear/customer-ui/allianz-cover-requests-card'
import { CustomerOfferForecastCard } from '@klear/customer-ui/customer-offer-forecast-card'
import {
  CUSTOMER_WIZARD_STEP,
  customerWizardStepFields,
  isCustomerLookupComplete,
  validateCustomerWizardStepValues,
  type CustomerWizardStepId
} from '@klear/customer-ui/customer-wizard-rules'
import { Timestamp } from '@klear/time'

export type CustomerStepGroupResult = { customerId: number; name: string }

export type CustomerFlowMode = 'create' | 'edit' | 'view'

export type CustomerFlowOptions = {
  traderId?: number
  showTraderSelect?: boolean
  showIdentifiers?: boolean
  showParentOrganization?: boolean
  showCompanyLookup?: boolean
  canViewCustomerCreditRatings?: boolean
  canEditCustomerCreditRatings?: boolean
}

const STEP = CUSTOMER_WIZARD_STEP

type StepId = CustomerWizardStepId

type CustomerFieldGroupProps = {
  values: CustomerWizardFormValues
  errors: EntityFormErrorMap
  onFieldChange: (path: CustomerFormFieldPath, value: CustomerFormFieldValue) => void
  onFormUpdate: CustomerFormUpdate
}

type CustomerStepCtx = {
  mode: CustomerFlowMode
  isEditing: boolean
  customerId?: number
  traderId?: number
  vals: CustomerWizardFormValues
  errors: EntityFormErrorMap
  showTraderSelect: boolean
  showIdentifiers: boolean
  showParentOrganization: boolean
  showCompanyLookup: boolean
  canViewCustomerCreditRatings: boolean
  canEditCustomerCreditRatings: boolean
  creditPreview: CreditRecord | null
  creditPreviewError: string | null
  isPreviewingCredit: boolean
  traderOptions: Array<{ value: number; label: string }>
  traderLabel?: string
  fieldGroupProps: CustomerFieldGroupProps
  handleFieldChange: (path: CustomerFormFieldPath, value: CustomerFormFieldValue) => void
  validateBeforeNext: (stepId: StepId) => Promise<boolean>
  advance: () => void
  getLookupComplete: () => boolean
  markLookupApplied: () => void
  allianzRefreshedAt?: Timestamp
  organizationId?: number
}

function stepFields(stepId: StepId): CustomerFormFieldPath[] {
  return customerWizardStepFields(stepId)
}

function validateStepValues(
  stepId: StepId,
  vals: CustomerWizardFormValues,
  showTraderSelect: boolean
): EntityFormErrorMap {
  return validateCustomerWizardStepValues(stepId, vals, showTraderSelect)
}

function moneyLabel(value: CustomerWizardFormValues['allianzCreditLimit']): string {
  return value?.toString() ?? '—'
}

function ReviewBody({
  values: v,
  showTraderSelect,
  showIdentifiers,
  showParentOrganization,
  canViewCustomerCreditRatings,
  traderLabel,
  allianzRefreshedAt
}: {
  values: CustomerWizardFormValues
  showTraderSelect: boolean
  showIdentifiers: boolean
  showParentOrganization: boolean
  canViewCustomerCreditRatings: boolean
  traderLabel?: string
  allianzRefreshedAt?: Timestamp
}) {
  const addr = v.address
  const addressLine = [addr?.line1, addr?.city, addr?.state, addr?.postalCode].filter(Boolean).join(', ')
  const allianzLinked = Boolean(v.allianzEhid?.trim())
  return (
    <Card>
      <CardHeader>
        <CardTitle>Review</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        {showTraderSelect && (
          <div>
            <span className="font-medium text-muted-foreground">Trader</span>
            <p>{traderLabel ?? '—'}</p>
          </div>
        )}
        <div>
          <span className="font-medium text-muted-foreground">Name</span>
          <p>{v.name || '—'}</p>
        </div>
        <div>
          <span className="font-medium text-muted-foreground">Description</span>
          <p>{v.description || '—'}</p>
        </div>
        {showParentOrganization && (
          <div>
            <span className="font-medium text-muted-foreground">Parent organization</span>
            <p>{v.parentOrganization || '—'}</p>
          </div>
        )}
        <div>
          <span className="font-medium text-muted-foreground">Country</span>
          <p>{v.address?.country ?? '—'}</p>
        </div>
        <div>
          <span className="font-medium text-muted-foreground">Address</span>
          <p>{addressLine || '—'}</p>
        </div>
        <div>
          <span className="font-medium text-muted-foreground">Website</span>
          <p>{v.website || '—'}</p>
        </div>
        {showIdentifiers && (
          <div className="grid grid-cols-2 gap-3 border-t pt-3">
            <div>
              <span className="font-medium text-muted-foreground">DUNS</span>
              <p>{v.dunsNumber || '—'}</p>
            </div>
            <div>
              <span className="font-medium text-muted-foreground">LEI</span>
              <p>{v.lei || '—'}</p>
            </div>
            <div>
              <span className="font-medium text-muted-foreground">EIN</span>
              <p>{v.ein || '—'}</p>
            </div>
            <div>
              <span className="font-medium text-muted-foreground">VAT</span>
              <p>{v.vatNumber || '—'}</p>
            </div>
          </div>
        )}
        {canViewCustomerCreditRatings && (
          <div className="space-y-2 border-t pt-3">
            <span className="font-medium text-muted-foreground">Credit</span>
            <p>Classification: {v.creditClassification ?? '—'}</p>
            {v.creditClassification === CREDIT_CLASSIFICATION_IG && (
              <p>
                S&amp;P {v.spRating ?? '—'} · Moody&apos;s {v.moodysRating ?? '—'} · Fitch {v.fitchRating ?? '—'}
              </p>
            )}
            {v.creditClassification === CREDIT_CLASSIFICATION_INSURED && (
              <p>
                Allianz {v.allianzRating ?? '—'} · Limit {moneyLabel(v.allianzCreditLimit)}
              </p>
            )}
          </div>
        )}
        {canViewCustomerCreditRatings && (
          <div className="grid grid-cols-3 gap-3 border-t pt-3">
            <div>
              <span className="font-medium text-muted-foreground">Allianz Linked</span>
              <p className="flex items-center gap-1.5">
                {allianzLinked ? (
                  <>
                    <Check className="h-4 w-4 text-green-600" aria-hidden />
                    True
                  </>
                ) : (
                  'False'
                )}
              </p>
            </div>
            <div>
              <span className="font-medium text-muted-foreground">Allianz EHID</span>
              <p>{v.allianzEhid || '—'}</p>
            </div>
            <div>
              <span className="font-medium text-muted-foreground">Refreshed</span>
              <p>{allianzRefreshedAt != null ? `${allianzRefreshedAt.toString('YYYY-MM-DD HH:mm')} UTC` : '—'}</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function CustomerTraderStep({ ctx }: { ctx: CustomerStepCtx }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Trader</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <Label htmlFor="customer-flow-trader">Trader</Label>
        <Combobox<number>
          value={ctx.vals.traderId ?? ''}
          valueType="number"
          options={ctx.traderOptions}
          disabled={ctx.mode !== 'create'}
          placeholder="Search traders..."
          onChange={(value) => ctx.handleFieldChange('traderId', value === '' ? undefined : value)}
          error={ctx.errors.traderId ? { message: ctx.errors.traderId } : undefined}
        />
        {ctx.errors.traderId ? <p className="text-sm text-destructive">{ctx.errors.traderId}</p> : null}
      </CardContent>
    </Card>
  )
}

function CustomerLookupStep({ ctx }: { ctx: CustomerStepCtx }) {
  return (
    <CompanyLookup
      values={ctx.vals}
      onFormUpdate={ctx.fieldGroupProps.onFormUpdate}
      required
      includeAllianzEhid
      showRatingStatus={ctx.canViewCustomerCreditRatings}
      overwriteEntityLabel="customer"
      confirmOverwrites={ctx.mode === 'edit'}
      onApplied={() => {
        ctx.markLookupApplied()
        ctx.advance()
      }}
    />
  )
}

function CustomerOrganizationStep({ ctx }: { ctx: CustomerStepCtx }) {
  return (
    <CustomerOrganizationFields
      {...ctx.fieldGroupProps}
      isEditing={ctx.isEditing}
      showParentOrganization={ctx.showParentOrganization}
    />
  )
}

function CustomerLocationStep({ ctx }: { ctx: CustomerStepCtx }) {
  return <CustomerLocationFields {...ctx.fieldGroupProps} isEditing={ctx.isEditing} />
}

function CustomerIdentifiersStep({ ctx }: { ctx: CustomerStepCtx }) {
  return <CustomerIdentifierFields {...ctx.fieldGroupProps} isEditing={ctx.isEditing} />
}

function CustomerCreditStep({ ctx }: { ctx: CustomerStepCtx }) {
  return (
    <CustomerCreditFields
      {...ctx.fieldGroupProps}
      isEditing={ctx.isEditing}
      creditEditable={ctx.canEditCustomerCreditRatings}
      organizationId={ctx.organizationId}
    />
  )
}

function CustomerReviewStep({ ctx }: { ctx: CustomerStepCtx }) {
  const reviewValues = ctx.creditPreview != null ? { ...ctx.vals, ...creditReadToForm(ctx.creditPreview) } : ctx.vals
  const allianzOrganizationId =
    ctx.mode === 'view' && ctx.canViewCustomerCreditRatings && ctx.vals.allianzEhid?.trim()
      ? ctx.organizationId
      : undefined
  return (
    <div className="space-y-4">
      {ctx.creditPreviewError ? <p className="text-sm text-destructive">{ctx.creditPreviewError}</p> : null}
      {ctx.isPreviewingCredit ? <p className="text-sm text-muted-foreground">Validating credit…</p> : null}
      <ReviewBody
        values={reviewValues}
        showTraderSelect={ctx.showTraderSelect}
        showIdentifiers={ctx.showIdentifiers}
        showParentOrganization={ctx.showParentOrganization}
        canViewCustomerCreditRatings={ctx.canViewCustomerCreditRatings}
        traderLabel={ctx.traderLabel}
        allianzRefreshedAt={ctx.allianzRefreshedAt}
      />
      {allianzOrganizationId != null && (
        <AllianzCoverRequestsCard
          organizationId={allianzOrganizationId}
          storedRating={ctx.vals.allianzRating}
          storedLimit={ctx.vals.allianzCreditLimit}
        />
      )}
      {ctx.mode === 'view' && ctx.customerId != null && ctx.customerId > 0 && (
        <CustomerOfferForecastCard customerId={ctx.customerId} traderId={ctx.vals.traderId || ctx.traderId} />
      )}
    </div>
  )
}

const CUSTOMER_STEP_MODULES: Array<WizardStepModule<CustomerStepCtx>> = [
  {
    id: STEP.TRADER,
    label: 'Trader',
    visible: (ctx) => ctx.showTraderSelect,
    beforeNext: (ctx) => ctx.validateBeforeNext(STEP.TRADER),
    component: CustomerTraderStep
  },
  {
    id: STEP.LOOKUP,
    label: 'Lookup',
    visible: (ctx) => ctx.showCompanyLookup,
    isComplete: (ctx) => ctx.getLookupComplete(),
    component: CustomerLookupStep
  },
  {
    id: STEP.ORGANIZATION,
    label: 'Organization',
    beforeNext: (ctx) => ctx.validateBeforeNext(STEP.ORGANIZATION),
    component: CustomerOrganizationStep
  },
  {
    id: STEP.LOCATION,
    label: 'Address',
    beforeNext: (ctx) => ctx.validateBeforeNext(STEP.LOCATION),
    component: CustomerLocationStep
  },
  {
    id: STEP.IDENTIFIERS,
    label: 'Identifiers',
    visible: (ctx) => ctx.showIdentifiers,
    beforeNext: (ctx) => ctx.validateBeforeNext(STEP.IDENTIFIERS),
    component: CustomerIdentifiersStep
  },
  {
    id: STEP.CREDIT,
    label: 'Credit',
    visible: (ctx) => ctx.canViewCustomerCreditRatings,
    beforeNext: (ctx) => ctx.validateBeforeNext(STEP.CREDIT),
    component: CustomerCreditStep
  },
  {
    id: STEP.REVIEW,
    label: 'Review',
    component: CustomerReviewStep
  }
]

export type CustomerFlowProps = CustomerFlowOptions & {
  mode: CustomerFlowMode
  customer?: CustomerRecord
  customerId?: number
  credit?: CreditRecord | null
  cancelOnFirstStep?: boolean
  onNavStateChange: (nav: WizardStepGroupNavState) => void
  onDirtyChange?: (dirty: boolean) => void
  onComplete: (result: CustomerStepGroupResult) => void
  onCancel: () => void
}

export function CustomerFlow({
  mode,
  customer,
  customerId,
  credit,
  traderId,
  showTraderSelect = false,
  showIdentifiers = false,
  showParentOrganization = false,
  showCompanyLookup = true,
  canViewCustomerCreditRatings = false,
  canEditCustomerCreditRatings = false,
  cancelOnFirstStep = false,
  onNavStateChange,
  onDirtyChange,
  onComplete,
  onCancel
}: CustomerFlowProps) {
  const isEditing = mode !== 'view'
  const includeCompanyLookup = (mode === 'create' || mode === 'edit') && showCompanyLookup
  const advanceRef = useRef<() => void>(noop)
  const advance = useCallback(() => {
    advanceRef.current()
  }, [])

  const lookupAppliedRef = useRef(false)
  const markLookupApplied = useCallback(() => {
    lookupAppliedRef.current = true
  }, [])

  const defaultValues = useMemo<CustomerWizardFormValues>(() => {
    if (customer) {
      return {
        ...customerToFormValues(customer, credit),
        traderId: customer.traderId,
        traderName: customer.traderName
      }
    }
    return { ...EMPTY_CUSTOMER_FORM, traderId, traderName: '' }
  }, [customer, credit, traderId])

  const form = useEntityWizardForm<
    CustomerWizardFormValues,
    CustomerWizardFormDirty,
    CustomerFormFieldPath,
    CustomerFormFieldValue,
    StepId
  >({
    defaultValues,
    setField: setCustomerFormField,
    markDirtyPaths: markCustomerFormDirtyPaths,
    clearErrors: clearCustomerFormErrors,
    stepFields,
    validateStep: (stepId, vals) => validateStepValues(stepId, vals, showTraderSelect),
    saveErrorFallback: 'Failed to save customer. Please try again.'
  })
  const { values: vals, errors, updateValues, handleFieldChange, validateBeforeNext } = form
  const valsRef = useRef(vals)
  valsRef.current = vals
  const getLookupComplete = useCallback(() => lookupAppliedRef.current || isCustomerLookupComplete(valsRef.current), [])
  const isDirty = isEditing && values(form.dirtyFields).some((v) => v === true)
  useEffect(() => {
    onDirtyChange?.(isDirty)
  }, [isDirty, onDirtyChange])

  const { traders } = useTraders()
  const { commitNewCustomer, commitCustomer, isCreating, isUpdating } = useCustomerMutations()
  const {
    previewCredit,
    commitNewCredit,
    commitCredit,
    isPreviewing: isPreviewingCredit,
    isCommitting: isCommittingCredit
  } = useCreditMutations()
  const isSaving = isCreating || isUpdating || isCommittingCredit

  const organizationId = credit?.organizationId ?? customer?.organizationId
  const submitMode = mode === 'edit' ? 'edit' : 'create'
  const needsCreditSubmit =
    isEditing && canEditCustomerCreditRatings && creditSubmitNeeded(submitMode, vals, form.dirtyFields)
  const canPreviewCreditOnReview = needsCreditSubmit && organizationId != null && organizationId > 0

  const [creditPreview, setCreditPreview] = useState<CreditRecord | null>(null)
  const [creditPreviewError, setCreditPreviewError] = useState<string | null>(null)

  const traderOptions = useMemo(() => traders?.map((t) => ({ value: t.id, label: t.name })) ?? [], [traders])

  const runCreditPreviewForReview = useCallback(
    async (previewOrganizationId: number) => {
      setCreditPreviewError(null)
      try {
        const previewed = await previewCredit({
          organizationId: previewOrganizationId,
          data: formValuesToCreditUpdateData(vals, form.dirtyFields)
        })
        setCreditPreview(previewed)
      } catch (err: unknown) {
        setCreditPreview(null)
        setCreditPreviewError(isError(err) ? err.message : 'Credit preview failed. Please check your entries.')
      }
    },
    [previewCredit, vals, form.dirtyFields]
  )
  const runCreditPreviewRef = useRef(runCreditPreviewForReview)
  runCreditPreviewRef.current = runCreditPreviewForReview

  const creditPreviewKey = canPreviewCreditOnReview
    ? JSON.stringify(formValuesToCreditUpdateData(vals, form.dirtyFields))
    : ''

  const handleSubmit = useCallback(async () => {
    const selectedTraderId = showTraderSelect ? vals.traderId : (traderId ?? customer?.traderId ?? vals.traderId)
    const validationErrors: EntityFormErrorMap = {}
    if (selectedTraderId == null || selectedTraderId < 1) {
      validationErrors[showTraderSelect ? 'traderId' : 'root'] = showTraderSelect
        ? 'Select a trader'
        : 'Missing trader context'
    }
    if (!vals.name.trim()) validationErrors.name = 'Customer name is required'
    if (mode === 'edit' && customerId == null) validationErrors.root = 'Missing customer context'
    if (canPreviewCreditOnReview && creditPreview == null) {
      validationErrors.root = creditPreviewError ?? 'Credit must be validated on Review before saving.'
    }
    if (hasEntityFormErrors(validationErrors)) {
      form.setErrors(validationErrors)
      return
    }
    await form.runSubmit(async () => {
      const result =
        mode === 'edit' && customerId != null
          ? await commitCustomer({
              id: customerId,
              traderId: selectedTraderId!,
              data: formValuesToUpdateData(vals, form.dirtyFields)
            })
          : await commitNewCustomer({ traderId: selectedTraderId!, data: formValuesToCreateData(vals) })
      if (result?.id == null) throw new Error('Saving the customer did not return an id.')

      const savedOrganizationId = result.organizationId
      if (
        savedOrganizationId != null &&
        savedOrganizationId > 0 &&
        canEditCustomerCreditRatings &&
        creditSubmitNeeded(submitMode, vals, form.dirtyFields)
      ) {
        try {
          if (submitMode === 'edit') {
            await commitCredit({
              organizationId: savedOrganizationId,
              data: formValuesToCreditUpdateData(vals, form.dirtyFields)
            })
          } else {
            await commitNewCredit({
              organizationId: savedOrganizationId,
              data: formValuesToCreditCreateData(vals)
            })
          }
        } catch {
          return
        }
      }

      form.clearDirty()
      onComplete({ customerId: result.id, name: result.name })
    })
  }, [
    creditPreview,
    creditPreviewError,
    showTraderSelect,
    vals,
    traderId,
    customer,
    mode,
    customerId,
    form,
    commitCustomer,
    commitNewCustomer,
    commitNewCredit,
    commitCredit,
    canEditCustomerCreditRatings,
    canPreviewCreditOnReview,
    submitMode,
    onComplete
  ])

  const fieldGroupProps = useMemo(
    () => ({
      values: vals,
      errors,
      onFieldChange: handleFieldChange,
      onFormUpdate: updateValues
    }),
    [vals, errors, handleFieldChange, updateValues]
  )

  const traderLabel = vals.traderId != null ? traderOptions.find((o) => o.value === vals.traderId)?.label : undefined

  const stepCtx = useMemo<CustomerStepCtx>(
    () => ({
      mode,
      isEditing,
      customerId,
      traderId,
      vals,
      errors,
      showTraderSelect,
      showIdentifiers,
      showParentOrganization,
      showCompanyLookup: includeCompanyLookup,
      canViewCustomerCreditRatings,
      canEditCustomerCreditRatings,
      creditPreview,
      creditPreviewError,
      isPreviewingCredit,
      traderOptions,
      traderLabel,
      fieldGroupProps,
      handleFieldChange,
      validateBeforeNext,
      advance,
      getLookupComplete,
      markLookupApplied,
      allianzRefreshedAt: customer?.allianzRefreshedAt,
      organizationId
    }),
    [
      mode,
      isEditing,
      customerId,
      traderId,
      vals,
      errors,
      showTraderSelect,
      showIdentifiers,
      showParentOrganization,
      includeCompanyLookup,
      canViewCustomerCreditRatings,
      canEditCustomerCreditRatings,
      creditPreview,
      creditPreviewError,
      isPreviewingCredit,
      traderOptions,
      traderLabel,
      fieldGroupProps,
      handleFieldChange,
      validateBeforeNext,
      advance,
      getLookupComplete,
      markLookupApplied,
      customer?.allianzRefreshedAt,
      organizationId
    ]
  )

  const stepDefs = useMemo(() => bindWizardSteps(CUSTOMER_STEP_MODULES, stepCtx), [stepCtx])
  const firstVisibleId = stepDefs.find((s) => s.visible !== false)?.id ?? STEP.ORGANIZATION

  const flow = useWizardFlow(stepDefs, {
    initialStepId: mode === 'view' ? STEP.REVIEW : firstVisibleId,
    onExitFirstStep: cancelOnFirstStep ? onCancel : undefined,
    stepSelection: mode === 'view' ? 'free' : 'gated'
  })

  useFlowWizardNav(onNavStateChange, flow)
  advanceRef.current = () => {
    void flow.goNext()
  }

  useEffect(() => {
    if (flow.activeStepId !== STEP.REVIEW) {
      setCreditPreview(null)
      setCreditPreviewError(null)
      return
    }
    if (!canPreviewCreditOnReview || organizationId == null) return
    void runCreditPreviewRef.current(organizationId)
  }, [flow.activeStepId, canPreviewCreditOnReview, organizationId, creditPreviewKey])

  const creditConfirmBlocked = isPreviewingCredit || (canPreviewCreditOnReview && creditPreview == null)

  const idleSubmitLabel = mode === 'edit' ? 'Confirm & save' : 'Create customer'
  const busySubmitLabel = isSaving ? 'Saving…' : isPreviewingCredit ? 'Validating…' : idleSubmitLabel

  return (
    <WizardFlowContent
      flow={flow}
      showNav={isEditing}
      onSubmit={() => void handleSubmit()}
      cancelOnFirstStep={cancelOnFirstStep}
      disabled={isSaving}
      submitDisabled={creditConfirmBlocked}
      nextIcon={mode === 'create' ? <Plus className="h-4 w-4 mr-1" /> : undefined}
      submitIcon={<Check className="h-4 w-4 mr-1" />}
      submitLabel={busySubmitLabel}
      navClassName="flex justify-between gap-2"
    >
      {form.saveError ? <p className="text-sm text-destructive">{form.saveError}</p> : null}
      {errors.root ? <p className="text-sm text-destructive">{errors.root}</p> : null}
    </WizardFlowContent>
  )
}
